import React, { useContext, useState } from 'react';
import { Container, Table, Button, InputGroup, FormControl, Collapse, Modal, Spinner } from 'react-bootstrap';
import UploadModalContent from './components/modal/upload-modal/upload-modal';
import './report-container.scss';
import ReportsService from '../../../../../../services/reports-service';
import { IdentityContext } from '../../../../../../context/identity-context';

const ReportContainer = ({ items }) => {

  const [searchTerm, setSearchTerm] = useState('');
  const [openRows, setOpenRows] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [generateMessage, setGenerateMessage] = useState('');
  const [sendMessage, setSendMessage] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [checkedReportIndexes, setCheckedReportIndexes] = useState(new Set());
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedComment, setSelectedComment] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [sendingReports, setSendingReports] = useState(false);

  const reportService = new ReportsService();
  const { user } = useContext(IdentityContext);

  const canGenerateReports = () => {
    return Array.from(checkedReportIndexes).every((key) => {
      const [itemId, reportIndex] = key.split('-');
      const selectedItem = items.find(item => item.id.toString() === itemId);
      const selectedReport = selectedItem.reports[reportIndex];
      return selectedReport.availability === 'Available' || selectedReport.availability === 'Not Available';
    });
  };

  const canSendReports = () => {
    return Array.from(checkedReportIndexes).every((key) => {
      const [itemId, reportIndex] = key.split('-');
      const selectedItem = items.find(item => item.id.toString() === itemId);
      const selectedReport = selectedItem.reports[reportIndex];
      return selectedReport.availability === 'Available' || selectedReport.availability === 'For Adjustment';
    });
  };

  const handleRowClick = (itemId) => {
    setOpenRows(prevOpenRows =>
      prevOpenRows.includes(itemId) ? prevOpenRows.filter(id => id !== itemId) : [...prevOpenRows, itemId]
    );
  };

  const handleUploadClick = (report, item) => {
    setSelectedReport(report);
    setSelectedItem(item);
    setShowUploadModal(true);
  };

  const handleCloseModal = () => {
    console.log("Closing modal"); 
    setShowUploadModal(false);
    setSelectedReport(null);
  };

  const handleCheckboxChange = (itemId, reportIndex, isChecked) => {
    const key = `${itemId}-${reportIndex}`;
    setCheckedReportIndexes(prevIndexes => {
      const updatedIndexes = new Set(prevIndexes);
      if (isChecked) {
        updatedIndexes.add(key);
      } else {
        updatedIndexes.delete(key);
      }
      return updatedIndexes;
    });
  };

  const handleCommentClick = (comment) => {
    setSelectedComment(comment);
    setShowCommentModal(true);
  };


  const handlePreviewClick = async (report, item) => {
    try {
      const response = await reportService.retrieveReport(report.reportType, item.isin, report.title, item.paymentDate);
      window.open(response['fileURL']);
      const newWindow = window.open(response.fileURL, '_blank', 'noopener,noreferrer');
      if (newWindow) newWindow.opener = null;
    } catch (error) {
      if (error.response) {
        console.error('Error fetching report preview:', error.response.data);
        console.error('Error status:', error.response.status);
        console.error('Error headers:', error.response.headers);
      } else {
        console.error('Error message:', error.message);
      }
    }
  };

  const handleDownloadClick = async (report, item) => {
    try {
      // Retrieve the report using the reportService
      const response = await reportService.retrieveReport(report.reportType, item.isin, report.title, item.paymentDate);

      const url = response.fileURL;
      const filename = response.fileName || url.substring(url.lastIndexOf('/') + 1); // Use fileName from response

      console.log(url, filename);

      const fileResponse = await fetch(url);
      if (!fileResponse.ok) throw new Error('Network response was not ok');

      const blob = await fileResponse.blob();
      const objectUrl = window.URL.createObjectURL(blob);

      // To download report
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename; // Use the correct filename
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);

    } catch (error) {
      if (error.response) {
        console.error('Error fetching report download:', error.response.data);
        console.error('Error status:', error.response.status);
        console.error('Error headers:', error.response.headers);
      } else {
        console.error('Error message:', error.message);
      }
    }
  };

  const handleGenerateClick = () => {
    const checkedReportsCount = checkedReportIndexes.size;
  
    if (checkedReportsCount > 0) {
      let allSuccess = true; // Track overall success
      checkedReportIndexes.forEach((key) => {
        const [itemId, reportIndex] = key.split('-');
        const selectedItem = items.find(item => item.id.toString() === itemId);
        const selectedReport = selectedItem.reports[reportIndex];
  
        reportService.generateReport(
          selectedReport.reportType,
          selectedReport.title,
          selectedItem.isin,
          selectedItem.paymentDate
        ).then(response => {
          console.log('Report Generated: ', response);
        }).catch(error => {
          console.error('Error generating report: ', error);
          allSuccess = false; // Set to false if any report generation fails
        });
      });
  
      if (allSuccess) {
        setIsSuccess(true); // Set to true if all reports are successfully generated
        setGenerateMessage(`Reports Successfully Generated\n${checkedReportsCount} reports have been successfully generated.`);
      } else {
        setIsSuccess(false);
        setGenerateMessage('There was an issue generating some reports.');
      }
    } else {
      setGenerateMessage('No Reports Generated\nThere are no reports generated for selected items.');
    }
  
    setShowGenerateModal(true);
  };
  
  const handleSendClick = () => {
    setSendingReports(true);
    const makerName = user.attributes['custom:display_name'];
    const checkedReportsCount = checkedReportIndexes.size;
    let successCount = 0; // Track the number of successfully sent reports
    
    if (checkedReportsCount > 0) {
      const sendPromises = Array.from(checkedReportIndexes).map((key) => {
        const [itemId, reportIndex] = key.split('-');
        const selectedItem = items.find(item => item.id.toString() === itemId);
        const selectedReport = selectedItem.reports[reportIndex];
  
        if (selectedReport && (selectedReport.availability === 'Available' || selectedReport.availability === 'For Adjustment')) {
          return reportService.sendReport(
            selectedItem.isin,
            selectedReport.title,
            selectedReport.reportType,
            selectedItem.paymentDate,
            makerName
          ).then(response => {
            console.log('Report Sent: ', response);
            successCount++; // Increment success count for each successful send
          }).catch(error => {
            console.error('Error sending report: ', error);
            // Optionally handle error logging here if needed
          });
        } else {
          // If the report is not available, resolve immediately
          return Promise.resolve();
        }
      });
  
      Promise.all(sendPromises).then(() => {
        setSendingReports(false); // Reset loading state
        if (successCount === checkedReportsCount) {
          setIsSuccess(true); // All reports sent successfully
          setSendMessage(`Reports Successfully Sent\n${checkedReportsCount} reports have been successfully sent.`);
        } else {
          setIsSuccess(false);
          setSendMessage(`Some reports were not sent. Successfully sent ${successCount} out of ${checkedReportsCount}.`);
        }
        setShowSendModal(true); // Show the send modal with the message
      }).catch(() => {
        setSendingReports(false); // Reset loading state on error
      });
    } else {
      setSendMessage('No Reports Sent\nThere are no reports sent for selected items.');
      setShowSendModal(true); // Show the send modal with the message
    }
  };
  
  
  const handleGenerateModalClose = () => {
    setShowGenerateModal(false);
    if (isSuccess) {
      window.location.reload(); // Refresh only if success
    }
  };
  
  const handleSendModalClose = () => {
    setShowSendModal(false);
    setSendingReports(false);
    if (isSuccess) {
      window.location.reload(); // Refresh only if success
    }
  };

  // Sorting function
  const sortedItems = [...items].sort((a, b) => {
    const dateA = new Date(a.paymentDate);
    const dateB = new Date(b.paymentDate);
    if (sortConfig.direction === 'asc') {
      return dateA - dateB;
    } else {
      return dateB - dateA;
    }
  });

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const filteredItems = sortedItems.filter(item => {
    const issuer = item.issuer ? item.issuer.toLowerCase() : '';
    const name = item.name ? item.name.toLowerCase() : '';
    const isin = item.isin ? item.isin.toLowerCase() : '';
    const paymentDate = item.paymentDate ? item.paymentDate.toLowerCase() : '';
    const status = item.status ? item.status.toLowerCase() : '';
    const searchLower = searchTerm.toLowerCase();

    return (
      issuer.includes(searchLower) ||
      name.includes(searchLower) ||
      isin.includes(searchLower) ||
      paymentDate.includes(searchLower) ||
      status.includes(searchLower)
    );
  });

  return (
    <Container className="report-container">
      {/* Search Bar */}
      <InputGroup className="mb-3">
        <FormControl
          placeholder="Search..."
          aria-label="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </InputGroup>

      {/* Content Area */}
      <div className="content-wrapper">
        <div className="content-scrollable">
          <Table bordered>
            <thead>
              <tr>
                <th>ISSUER</th>
                <th>SECURITY NAME</th>
                <th>ISIN</th>
                <th onClick={() => handleSort('paymentDate')} style={{ cursor: 'pointer' }}>
                  Payment Date {sortConfig.key === 'paymentDate' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                </th>
                <th>STATUS</th>
              </tr>
            </thead>
              <tbody>
                {filteredItems.map(item => (
                  <React.Fragment key={item.id}>
                    <tr onClick={() => handleRowClick(item.id)}>
                      <td>{item.issuer}</td>
                      <td>{item.name}</td>
                      <td>{item.isin}</td>
                      <td>{item.paymentDate}</td>
                      <td>{item.status}</td>
                    </tr>
                    <tr>
                      <Collapse in={openRows.includes(item.id)}>
                        <td colSpan={5}>
                          <Table bordered className="collapse-table">
                            <tbody>
                              {item.reports.length > 0 ? (
                                item.reports.map((report, idx) => (
                                  <tr key={idx}>
                                    <td>
                                      <input
                                        type="checkbox"
                                        checked={checkedReportIndexes.has(`${item.id}-${idx}`)}
                                        onChange={(e) =>
                                          handleCheckboxChange(item.id, idx, e.target.checked)
                                        }
                                        disabled={
                                          report.availability !== 'Available' &&
                                          report.availability !== 'Not Available' &&
                                          report.availability !== 'For Adjustment'
                                        }
                                      />
                                    </td>
                                    <td>{report.title}</td>
                                    <td>
                                      {report.availability}
                                      {report.comment? <span
                                        style={{ cursor: 'pointer', marginLeft: '5px' }}
                                        onClick={() => handleCommentClick(report.comment)}
                                      >
                                        ▼
                                      </span>
                                      :
                                      ''}
                                    </td>
                                    <td>
                                      {/* Conditionally render the buttons based on report availability */}
                                      {report.availability === 'Not Available' ? null :
                                        (report.availability === 'Available' || report.availability === 'For Adjustment') ? (
                                          <>
                                            <Button
                                              variant="primary"
                                              className='action-button'
                                              onClick={() => handleUploadClick(report, item)}
                                            >
                                              Upload
                                            </Button>
                                            <Button
                                              variant="primary"
                                              className='action-button'
                                              onClick={() => handlePreviewClick(report, item)}
                                            >
                                              Preview
                                            </Button>
                                            <Button
                                              variant="primary"
                                              className='action-button'
                                              onClick={() => handleDownloadClick(report, item)}
                                            >
                                              Download
                                            </Button>
                                          </>
                                        ) : (
                                          <>
                                            <Button
                                              variant="primary"
                                              className='action-button'
                                              onClick={() => handlePreviewClick(report, item)}
                                            >
                                              Preview
                                            </Button>
                                            <Button
                                              variant="primary"
                                              className='action-button'
                                              onClick={() => handleDownloadClick(report, item)}
                                            >
                                              Download
                                            </Button>
                                          </>
                                        )}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={4} style={{ textAlign: 'center' }}>No reports</td>
                                </tr>
                              )}
                            </tbody>
                          </Table>
                        </td>
                      </Collapse>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
          </Table>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="action-buttons">
        <Button
          variant="secondary"
          className="me-2"
          onClick={handleGenerateClick}
          disabled={!canGenerateReports()}
        >
          Generate Reports
        </Button>
        <Button
          variant="primary"
          onClick={handleSendClick}
          disabled={!canSendReports() || sendingReports}
        >
          {sendingReports ? (
              <>
                <Spinner animation="border" style={{ width: "20px", height: "20px" }}></Spinner> 
              </>
            ) : (
              "Send Reports"
            )}
        </Button>
      </div>

      {/* Upload Modal */}
      {selectedReport && selectedItem && (
        <Modal
          show={showUploadModal}
          onHide={handleCloseModal}
          dialogClassName='maker-upload-modal modal-dialog-centered'
        >
          <Modal.Header closeButton>
            <Modal.Title>Upload Document</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <UploadModalContent context={{ report: selectedReport, item: selectedItem }} onClose = {handleCloseModal} />
          </Modal.Body>
        </Modal>
      )}

      {/* Generate Report Modal */}
      <Modal
        show={showGenerateModal}
        onHide={handleGenerateModalClose}
        backdrop="static"
        dialogClassName='generate-modal modal-dialog-centered'
      >
        <Modal.Header closeButton>
          <Modal.Title>Generate Report</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{generateMessage}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleGenerateModalClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Send Report Modal */}
      <Modal
        show={showSendModal}
        onHide={handleSendModalClose}
        backdrop="static"
        dialogClassName='send-report-modal modal-dialog-centered'
      >
        <Modal.Header closeButton>
          <Modal.Title>Send Report</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{sendMessage}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleSendModalClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Comment Modal */}
      <Modal
        show={showCommentModal}
        onHide={() => setShowCommentModal(false)}
        backdrop="static"
        dialogClassName='commentModal modal-dialog-centered'
      >
        <Modal.Header closeButton>
          <Modal.Title>Report Comment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{selectedComment? selectedComment : 'No Comments'}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCommentModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
};

export default ReportContainer;
